/**
 * Parameter Count Validator Utility
 * Ensures accurate parameter counting and validation against parameters.md
 */

/**
 * Expected parameter count from parameters.md (authoritative source)
 * Actual count verified by comprehensive analysis: 504 unique parameters
 * This is the authoritative count from parameters.md as the single source of truth
 * This should be updated whenever parameters.md is modified
 *
 * Note: Updated count based on actual parameters.md content:
 * - 504 total unique parameter entries in parameters.md (includes all parameter categories)
 * - This count represents the definitive parameter list for Care360
 * - All configuration files must align with this count (no more, no less)
 */
export const EXPECTED_PARAMETER_COUNT = 504;

/**
 * Validates the total parameter count against the expected count from parameters.md
 * @param {Array} tableData - The processed table data from useHealthSnapshotData
 * @returns {Object} Validation results with detailed breakdown
 */
export const validateParameterCount = (tableData) => {
  if (!tableData || !Array.isArray(tableData)) {
    return {
      isValid: false,
      actualCount: 0,
      expectedCount: EXPECTED_PARAMETER_COUNT,
      discrepancy: EXPECTED_PARAMETER_COUNT,
      error: 'No table data provided',
      categoryBreakdown: {}
    };
  }

  // Calculate actual parameter count
  const categoryBreakdown = {};
  let actualCount = 0;

  tableData.forEach(categoryGroup => {
    const categoryName = categoryGroup.categoryName || 'Unknown';
    const paramCount = categoryGroup.parameters ? categoryGroup.parameters.length : 0;
    categoryBreakdown[categoryName] = paramCount;
    actualCount += paramCount;
  });

  const discrepancy = actualCount - EXPECTED_PARAMETER_COUNT;
  const isValid = discrepancy === 0;

  return {
    isValid,
    actualCount,
    expectedCount: EXPECTED_PARAMETER_COUNT,
    discrepancy,
    categoryBreakdown,
    error: null
  };
};

/**
 * Analyzes parameter inclusion in Overall Wellness Score calculation
 * @param {Array} tableData - The processed table data from useHealthSnapshotData
 * @returns {Object} Analysis of parameter inclusion in calculations
 */
export const analyzeParameterInclusion = (tableData) => {
  if (!tableData || !Array.isArray(tableData)) {
    return {
      totalParameters: 0,
      parametersWithData: 0,
      parametersWithValidZScores: 0,
      parametersWithErrors: 0,
      inclusionRate: 0,
      exclusionReasons: {}
    };
  }

  let totalParameters = 0;
  let parametersWithData = 0;
  let parametersWithValidZScores = 0;
  let parametersWithErrors = 0;
  const exclusionReasons = {
    'No patient data': 0,
    'Calculation error': 0,
    'Missing ideal target': 0,
    'Invalid Z-score': 0,
    'Informational only': 0
  };

  tableData.forEach(categoryGroup => {
    if (categoryGroup.parameters) {
      categoryGroup.parameters.forEach(param => {
        totalParameters++;

        // Check if parameter has patient data
        if (param.currentValue !== null && param.currentValue !== undefined) {
          parametersWithData++;
        } else {
          exclusionReasons['No patient data']++;
        }

        // Check if parameter has valid Z-score for Overall Wellness Score
        if (param.zScore !== null && param.zScore !== undefined && 
            typeof param.zScore === 'number' && !isNaN(param.zScore) && !param.error) {
          parametersWithValidZScores++;
        } else {
          // Categorize exclusion reasons
          if (param.error) {
            if (param.error === "Awaiting data") {
              exclusionReasons['No patient data']++;
            } else if (param.error.includes('target')) {
              exclusionReasons['Missing ideal target']++;
            } else if (param.error.includes('Informational')) {
              exclusionReasons['Informational only']++;
            } else {
              exclusionReasons['Calculation error']++;
              parametersWithErrors++;
            }
          } else if (isNaN(param.zScore) || param.zScore === null) {
            exclusionReasons['Invalid Z-score']++;
          }
        }
      });
    }
  });

  const inclusionRate = totalParameters > 0 ? (parametersWithValidZScores / totalParameters) * 100 : 0;

  return {
    totalParameters,
    parametersWithData,
    parametersWithValidZScores,
    parametersWithErrors,
    inclusionRate,
    exclusionReasons
  };
};

/**
 * Generates a comprehensive parameter count report
 * @param {Array} tableData - The processed table data from useHealthSnapshotData
 * @param {Object} overallDeviation - The overall deviation object from useHealthSnapshotData
 * @returns {Object} Comprehensive report
 */
export const generateParameterCountReport = (tableData, overallDeviation) => {
  const countValidation = validateParameterCount(tableData);
  const inclusionAnalysis = analyzeParameterInclusion(tableData);

  const report = {
    timestamp: new Date().toISOString(),
    countValidation,
    inclusionAnalysis,
    overallDeviationCount: overallDeviation?.count || 0,
    recommendations: []
  };

  // Generate recommendations based on analysis
  if (!countValidation.isValid) {
    if (countValidation.discrepancy > 0) {
      report.recommendations.push({
        type: 'warning',
        message: `${countValidation.discrepancy} more parameters found than expected. Check for duplicates or new parameters not in parameters.md.`
      });
    } else {
      report.recommendations.push({
        type: 'error',
        message: `${Math.abs(countValidation.discrepancy)} parameters missing. Check configuration files and parameter naming conventions.`
      });
    }
  }

  if (inclusionAnalysis.inclusionRate < 50) {
    report.recommendations.push({
      type: 'critical',
      message: `Only ${inclusionAnalysis.inclusionRate.toFixed(1)}% of parameters are included in Overall Wellness Score. This significantly undermines accuracy.`
    });
  } else if (inclusionAnalysis.inclusionRate < 80) {
    report.recommendations.push({
      type: 'warning',
      message: `${inclusionAnalysis.inclusionRate.toFixed(1)}% of parameters included in Overall Wellness Score. Consider improving data collection or parameter configuration.`
    });
  }

  return report;
};

/**
 * Logs a detailed parameter count report to console
 * @param {Array} tableData - The processed table data from useHealthSnapshotData
 * @param {Object} overallDeviation - The overall deviation object from useHealthSnapshotData
 * @param {boolean} verbose - Whether to show detailed breakdown (default: false for performance)
 */
export const logParameterCountReport = (tableData, overallDeviation, verbose = false) => {
  const report = generateParameterCountReport(tableData, overallDeviation);

  console.log('\n' + '='.repeat(80));
  console.log('📊 CARE360 PARAMETER COUNT VALIDATION REPORT');
  console.log('='.repeat(80));
  
  console.log(`\n📋 Parameter Count Validation:`);
  console.log(`   Expected (parameters.md): ${report.countValidation.expectedCount}`);
  console.log(`   Actual (processed): ${report.countValidation.actualCount}`);
  console.log(`   Discrepancy: ${report.countValidation.discrepancy}`);
  console.log(`   Status: ${report.countValidation.isValid ? '✅ VALID' : '❌ INVALID'}`);

  console.log(`\n🎯 Overall Wellness Score Inclusion:`);
  console.log(`   Total parameters: ${report.inclusionAnalysis.totalParameters}`);
  console.log(`   Parameters with data: ${report.inclusionAnalysis.parametersWithData}`);
  console.log(`   Parameters in calculation: ${report.inclusionAnalysis.parametersWithValidZScores}`);
  console.log(`   Inclusion rate: ${report.inclusionAnalysis.inclusionRate.toFixed(1)}%`);
  console.log(`   Overall deviation count: ${report.overallDeviationCount}`);

  // Only show detailed breakdown if verbose mode is enabled
  if (verbose) {
    console.log(`\n📊 Category Breakdown:`);
    Object.entries(report.countValidation.categoryBreakdown).forEach(([category, count]) => {
      console.log(`   ${category}: ${count} parameters`);
    });

    console.log(`\n⚠️ Exclusion Reasons:`);
    Object.entries(report.inclusionAnalysis.exclusionReasons).forEach(([reason, count]) => {
      if (count > 0) {
        console.log(`   ${reason}: ${count} parameters`);
      }
    });
  } else {
    // Condensed summary for performance
    const topExclusionReasons = Object.entries(report.inclusionAnalysis.exclusionReasons)
      .filter(([reason, count]) => count > 0)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3);

    if (topExclusionReasons.length > 0) {
      console.log(`\n⚠️ Top exclusion reasons: ${topExclusionReasons.map(([reason, count]) => `${reason} (${count})`).join(', ')}`);
    }
  }

  if (report.recommendations.length > 0) {
    console.log(`\n💡 Recommendations:`);
    report.recommendations.forEach((rec, index) => {
      const icon = rec.type === 'critical' ? '🚨' : rec.type === 'error' ? '❌' : '⚠️';
      console.log(`   ${index + 1}. ${icon} ${rec.message}`);
    });
  }

  console.log('\n' + '='.repeat(80));
  
  return report;
};
